import "./webgl-maplibre.worker-url";
import { GeoJSONSource, GeoJSONSourceSpecification, Map as MlMap, StyleSpecification } from "maplibre-gl";
import { getAeroPlaneHeight } from "./webgl-maplibre.aero-plane";
import { hideVectorLabelsDuringRotation } from "./webgl-maplibre.rotation-labels";
import { pruneLabelImages } from "./webgl-maplibre.airspace-labels";

const SOURCE_IDS = ["vectormap", "airspace-labels"];
const empty = (): GeoJSON.FeatureCollection => ({ type: "FeatureCollection", features: [] });
export function isPlaneFeature(feature: GeoJSON.Feature): boolean {
  const p = feature.properties ?? {};
  if (p.mountainPass === true) return false;
  // Keep obstacles and webcam cones with their terrain-based 3D renderer and
  // 2D fallback, alongside airport/heliport, runway/FATO, apron and taxiway.
  if ([1, 4, 6, 11, 12, 13, 14, 15].includes(p._objType)) return false;
  if (["ais", "windsock", "landing-t"].includes(p.miscSymbol)) return false;
  return true;
}

/** Transparent, terrain-free vector rendering preserves native line styles and label placement. */
export class VectorPlane {
  private overlay: MlMap;
  private container: HTMLDivElement;
  private ready = false;
  private removed = false;
  private data = new Map<string, GeoJSON.FeatureCollection>();
  private cameraKey = "";
  private hasContent = true;
  private populatedSources = new Set<string>();
  private removeRotationLabels?: () => void;

  private readonly sourceIds = SOURCE_IDS;

  constructor(private readonly map: MlMap) {
    const style = map.getStyle();
    for (const id of this.sourceIds) {
      const source = map.getSource(id)?.serialize() as GeoJSONSourceSpecification | undefined;
      this.data.set(id, typeof source?.data === "object" ? (source.data as GeoJSON.FeatureCollection) : empty());
    }
    this.container = document.createElement("div");
    Object.assign(this.container.style, { position: "absolute", inset: "0", pointerEvents: "none" });
    this.container.setAttribute("aria-hidden", "true");
    // Above the main canvas, below map controls and popups.
    map.getCanvasContainer().appendChild(this.container);
    const overlayStyle: StyleSpecification = {
      version: 8,
      ...(style.glyphs ? { glyphs: style.glyphs } : {}),
      sources: Object.fromEntries(this.sourceIds.map((id) => [id, { type: "geojson", data: empty() }])),
      layers: style.layers.filter((layer) => "source" in layer && this.sourceIds.includes(String(layer.source))),
    };
    this.overlay = new MlMap({
      container: this.container,
      style: overlayStyle,
      interactive: false,
      attributionControl: false,
      maxPitch: 85,
      fadeDuration: 0,
      center: map.getCenter(),
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
      // This is a display plane, not a separate navigable camera. In particular,
      // do not let the overlay's sea-level collision correction change pitch/zoom.
      transformCameraUpdate: () => this.cameraOptions(),
    });
    this.overlay.setCenterClampedToGround(false);
    this.overlay.on("styleimagemissing", (event) => this.copyImage(event.id));
    this.overlay.on("load", () => {
      if (this.removed) return;
      this.ready = true;
      this.removeRotationLabels = hideVectorLabelsDuringRotation(this.map, this.overlay);
      for (const id of this.sourceIds) this.updateSource(id);
      this.syncCamera();
    });
    map.on("render", this.syncCamera);
    map.on("resize", this.resize);
    this.syncCamera();
  }

  /** Keep airports and obstacles with terrain; put the remaining chart features on the sheet. */
  setData(id: string, data: GeoJSON.FeatureCollection): boolean {
    if (!this.sourceIds.includes(id)) return false;
    const previous = this.data.get(id);
    if (
      this.ready &&
      previous?.features.length === data.features.length &&
      previous.features.every((feature, index) => feature === data.features[index])
    )
      return true;
    this.data.set(id, data);
    if (!this.ready) return false;
    this.updateSource(id);
    return true;
  }

  private updateSource(id: string): void {
    const data = this.data.get(id) ?? empty();
    // Keep every navigation feature on its flat route plane, including waypoint labels.
    const raised = id !== "vectormap" ? data : { ...data, features: data.features.filter(isPlaneFeature) };
    const ground =
      id !== "vectormap" ? empty() : { ...data, features: data.features.filter((f) => !isPlaneFeature(f)) };
    this.setSourcePopulated(id, raised.features.length > 0);
    (this.overlay.getSource(id) as GeoJSONSource)?.setData(raised);
    // The overlay holds its own copies of the label images (copyImage), so prune them here too.
    if (id === "airspace-labels") pruneLabelImages(this.overlay, raised);
    (this.map.getSource(id) as GeoJSONSource)?.setData(ground);
  }

  private copyImage(id: string): void {
    if (this.overlay.hasImage(id) || !this.map.hasImage(id)) return;
    const image = this.map.getImage(id);
    this.overlay.addImage(id, image.data, {
      pixelRatio: image.pixelRatio,
      sdf: image.sdf,
      stretchX: image.stretchX,
      stretchY: image.stretchY,
      content: image.content,
    });
  }

  private setSourcePopulated(id: string, populated: boolean): void {
    if (populated) this.populatedSources.add(id);
    else this.populatedSources.delete(id);
    this.hasContent = this.populatedSources.size > 0;
    this.container.style.display = this.hasContent ? "" : "none";
    if (this.hasContent) this.cameraKey = "";
  }

  private cameraOptions() {
    const center = this.map.getCenter();
    // Lower the overlay camera by the sheet altitude: its z=0 vectors then
    // project exactly like the main camera's vectors at the shared plane height.
    return {
      center,
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
      roll: this.map.getRoll(),
      elevation: this.map.getCenterElevation() - getAeroPlaneHeight(this.map),
      padding: this.map.getPadding(),
    };
  }

  private syncCamera = (): void => {
    if (this.removed || (this.ready && !this.hasContent)) return;
    const camera = this.cameraOptions();
    const key = JSON.stringify(camera);
    if (key === this.cameraKey) return;
    this.cameraKey = key;
    this.overlay.jumpTo(camera);
    // Both canvases must reach the browser compositor in the same animation
    // frame. jumpTo alone schedules the overlay for the next frame, producing
    // a visible lag/catch-up while the terrain camera moves.
    if (this.ready) this.overlay.redraw();
  };

  private resize = (): void => {
    this.overlay.resize();
    this.cameraKey = "";
    this.syncCamera();
  };

  remove(): void {
    this.removed = true;
    this.removeRotationLabels?.();
    this.map.off("render", this.syncCamera);
    this.map.off("resize", this.resize);
    this.overlay.remove();
    this.container.remove();
    for (const [id, data] of this.data) (this.map.getSource(id) as GeoJSONSource)?.setData(data);
    this.data.clear();
  }
}
